import {Injectable} from '@angular/core';
import Keycloak from 'keycloak-js';
import {BehaviorSubject, firstValueFrom} from 'rxjs';
import {SdkBridge} from '../../../src/app/sdk-bridge';

/**
 * Serializable token subset shared between tabs.
 * We keep it in-memory and transfer it over BroadcastChannel only.
 */
interface SharedTokenState {
    token: string;
    refreshToken: string;
    idToken?: string;
    timeSkew?: number;
}

/**
 * Message sent by a tab that needs an up-to-date token state from peers.
 */
interface TokenRequestMessage {
    type: 'token-request';
    requestId: string;
    fromTabId: string;
}

/**
 * Message carrying token state.
 * - With requestId: response to a specific token request.
 * - Without requestId: proactive broadcast after login/refresh.
 */
interface TokenStateMessage {
    type: 'token-state';
    requestId?: string;
    fromTabId: string;
    state: SharedTokenState;
}

/**
 * BroadcastChannel message union used for cross-tab auth synchronization.
 */
type KeycloakTabMessage = TokenRequestMessage | TokenStateMessage;

/**
 * Keycloak service
 */
@Injectable({
    providedIn: 'root'
})
export class KeycloakService {
    private keycloak?: Keycloak;
    private isInitialized = new BehaviorSubject<boolean>(false);
    /**
     * Guards against concurrent refresh calls in the same tab.
     * Keycloak refresh token usage is sensitive to races; sharing one promise avoids duplicate refresh attempts.
     */
    private refreshInFlight?: Promise<boolean>;
    /**
     * Unique runtime identifier used to ignore our own BroadcastChannel messages.
     */
    private readonly tabId = this.generateTabId();
    /**
     * Cross-tab channel dedicated to auth state synchronization.
     */
    private readonly channelName = 'oo8-keycloak-auth-sync';
    private syncChannel?: BroadcastChannel;
    /**
     * Pending requestId resolvers waiting for a token-state response from peers.
     */
    private tokenRequestResolvers = new Map<string, (state?: SharedTokenState) => void>();
    /**
     * Local fallback timer that mimics keycloak-js token-expiry behavior
     * when token state is injected from another tab.
     */
    private tokenExpiryTimeoutHandle?: ReturnType<typeof setTimeout>;
    public isInitialized$ = this.isInitialized.asObservable();

    // ===============================================================
    // ===============================================================

    /**
     * Initializes the Keycloak client at app startup.
     *
     * Silent SSO flow used here:
     * 1) `onLoad: 'check-sso'` asks Keycloak if a server-side SSO session already exists.
     * 2) `silentCheckSsoRedirectUri` points to `/silent-check-sso.html`, loaded in a hidden iframe.
     * 3) Keycloak redirects that iframe and checks its own httpOnly cookie (no localStorage token persistence).
     * 4) The iframe posts the auth response URL back to this app so keycloak-js can complete auth in memory.
     *
     * Result:
     * - F5 / hard reload restores the existing SSO session without forcing login when session is still valid.
     * - If no SSO session exists, init returns `false` and the app performs the explicit login redirect.
     */
    public async init(): Promise<boolean> {
        try {
            const config = await firstValueFrom(SdkBridge.fetchKeycloakConfig());

            // Convert from API format to Keycloak format
            this.keycloak = new Keycloak({
                url: config.url,
                realm: config.realm,
                clientId: config.client_id
            });

            // Reactive refresh in case token expires while the tab stays open.
            this.keycloak.onTokenExpired = () => {
                console.log('Token expired, refreshing...');
                void this.updateToken();
            };
            this.setupCrossTabSync();

            const peerTokenState = await this.requestTokenStateFromPeers();

            // `checkLoginIframe: true` enables Keycloak session-state monitoring.
            // This helps detect logout/session changes that happen outside the current tab.
            const authenticated = await this.keycloak.init({
                onLoad: 'check-sso',
                checkLoginIframe: true,
                silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
                token: peerTokenState?.token,
                refreshToken: peerTokenState?.refreshToken,
                idToken: peerTokenState?.idToken,
                timeSkew: peerTokenState?.timeSkew
            });

            console.log('Keycloak initialized, authenticated:', authenticated);
            this.isInitialized.next(true);
            if (authenticated) {
                this.broadcastTokenState();
            }

            return authenticated;
        } catch (error) {
            throw 'Keycloak init error: ' + error;
        }
    }

    // ===============================================================
    // ===============================================================

    /**
     * Get Keycloak token
     * @returns Keycloak token or undefined
     */
    public getToken(): string | undefined {
        if (!this.keycloak) {
            console.log('Keycloak is not ready yet');
            return undefined;
        }

        return this.keycloak.token;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Redirect user to the login page
     */
    public redirectToLogin(): void {
        if (!this.keycloak) {
            console.log('Keycloak is not ready yet');
            return;
        }

        this.keycloak.login();
    }
    /**
     * Redirect user to the logout page
     * @param redirectUri Redirection URI after logout
     */
    public redirectToLogout(redirectUri?: string): void {
        if (!this.keycloak) {
            console.log('Keycloak is not ready yet');
            return;
        }

        if (window.location.pathname === '/not-found') {
            redirectUri = '/';
        }

        this.keycloak.logout({
            redirectUri: redirectUri
                ? window.location.origin + redirectUri
                : window.location.origin + window.location.pathname
        });
    }

    // ===============================================================
    // ===============================================================

    /**
     * Get internal Keycloak instance
     * @returns Internal Keycloak instance or undefined
     */
    public getKeycloakInstance(): Keycloak | undefined {
        return this.keycloak;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Refresh Keycloak token if it expires within minValidity seconds.
     * No network call is made when the token is still sufficiently valid.
     * Called proactively by the HTTP interceptor before every backend request.
     * @param minValidity seconds of validity below which a refresh is triggered
     * @returns True if the token was refreshed
     */
    public updateToken(minValidity = 60): Promise<boolean> {
        if (!this.keycloak) {
            console.log('Keycloak is not ready yet');
            return Promise.resolve(false);
        }

        // Reuse current refresh request when one is already running.
        // Prevents duplicate concurrent refreshes from interceptor + token-expiry callback.
        if (this.refreshInFlight) {
            return this.refreshInFlight;
        }

        this.refreshInFlight = this.keycloak
            .updateToken(minValidity)
            .then((refreshed) => {
                if (refreshed) {
                    console.log('Token refreshed');
                    this.broadcastTokenState();
                }

                return refreshed;
            })
            .catch(async (error) => {
                // Refresh token expired or revoked — session is unrecoverable
                console.error('Failed to refresh token, trying cross-tab recovery', error);
                if (await this.tryRecoverFromOtherTab(minValidity)) {
                    return true;
                }
                console.error('Cross-tab recovery failed, redirecting to login');
                await this.keycloak?.login();
                return false;
            })
            .finally(() => {
                this.refreshInFlight = undefined;
            });

        return this.refreshInFlight;
    }

    /**
     * Recovers auth state from another tab then retries refresh once.
     */
    private async tryRecoverFromOtherTab(minValidity: number): Promise<boolean> {
        const recoveredState = await this.requestTokenStateFromPeers(1200);
        if (!recoveredState || !this.keycloak) {
            return false;
        }

        this.applyTokenState(recoveredState);

        try {
            const refreshed = await this.keycloak.updateToken(minValidity);
            this.broadcastTokenState();
            return refreshed || this.keycloak.token != null;
        } catch (retryError) {
            console.error('Token refresh still failing after peer recovery', retryError);
            return false;
        }
    }

    /**
     * Initializes BroadcastChannel-based tab synchronization.
     * Also refreshes token when a background tab becomes visible again.
     */
    private setupCrossTabSync(): void {
        if (typeof BroadcastChannel === 'undefined' || this.syncChannel) {
            return;
        }

        this.syncChannel = new BroadcastChannel(this.channelName);
        this.syncChannel.onmessage = (event: MessageEvent<KeycloakTabMessage>) => {
            this.handleSyncMessage(event.data);
        };

        document.addEventListener('visibilitychange', () => {
            if (!document.hidden) {
                void this.updateToken();
            }
        });
    }

    /**
     * Handles inbound cross-tab messages:
     * - token-request => respond with local token state when authenticated
     * - token-state => optionally apply fresher state + resolve pending request
     */
    private handleSyncMessage(message: KeycloakTabMessage): void {
        if (!message || message.fromTabId === this.tabId) {
            return;
        }

        if (message.type === 'token-request') {
            const state = this.getCurrentTokenState();
            if (!state || !this.syncChannel) {
                return;
            }

            const response: TokenStateMessage = {
                type: 'token-state',
                requestId: message.requestId,
                fromTabId: this.tabId,
                state
            };
            this.syncChannel.postMessage(response);
            return;
        }

        if (message.type === 'token-state') {
            if (this.shouldApplyIncomingState(message.state)) {
                this.applyTokenState(message.state);
            }

            if (message.requestId) {
                const resolver = this.tokenRequestResolvers.get(message.requestId);
                if (resolver) {
                    this.tokenRequestResolvers.delete(message.requestId);
                    resolver(message.state);
                }
            }
        }
    }

    /**
     * Requests token state from other tabs and waits for first response.
     * Returns undefined on timeout when no peer can provide a token.
     */
    private async requestTokenStateFromPeers(timeoutMs = 500): Promise<SharedTokenState | undefined> {
        if (!this.syncChannel) {
            return undefined;
        }

        const requestId = this.generateTabId();
        const request: TokenRequestMessage = {
            type: 'token-request',
            requestId,
            fromTabId: this.tabId
        };

        const responsePromise = new Promise<SharedTokenState | undefined>((resolve) => {
            const timeoutHandle = setTimeout(() => {
                this.tokenRequestResolvers.delete(requestId);
                resolve(undefined);
            }, timeoutMs);

            this.tokenRequestResolvers.set(requestId, (state?: SharedTokenState) => {
                clearTimeout(timeoutHandle);
                resolve(state);
            });
        });

        this.syncChannel.postMessage(request);
        return responsePromise;
    }

    /**
     * Applies only strictly fresher access tokens to avoid regressing local state.
     */
    private shouldApplyIncomingState(incoming: SharedTokenState): boolean {
        const incomingExp = this.extractTokenExp(incoming.token);
        if (!incomingExp) {
            return false;
        }

        const currentExp = this.extractTokenExp(this.keycloak?.token);
        return !currentExp || incomingExp > currentExp;
    }

    /**
     * Hydrates keycloak-js runtime fields from peer state and re-arms expiry refresh.
     * This is intentionally in-memory (no localStorage persistence).
     */
    private applyTokenState(state: SharedTokenState): void {
        if (!this.keycloak) {
            return;
        }

        this.keycloak.token = state.token;
        this.keycloak.refreshToken = state.refreshToken;
        this.keycloak.idToken = state.idToken;
        this.keycloak.timeSkew = state.timeSkew;

        this.keycloak.tokenParsed = this.parseJwt(state.token);
        this.keycloak.refreshTokenParsed = this.parseJwt(state.refreshToken);
        this.keycloak.idTokenParsed = state.idToken ? this.parseJwt(state.idToken) : undefined;

        this.keycloak.authenticated = true;
        this.keycloak.subject = this.keycloak.tokenParsed?.sub;
        this.keycloak.realmAccess = this.keycloak.tokenParsed?.realm_access;
        this.keycloak.resourceAccess = this.keycloak.tokenParsed?.resource_access;

        this.scheduleTokenExpiryRefresh();
    }

    /**
     * Schedules refresh at token expiration time when token was injected manually.
     */
    private scheduleTokenExpiryRefresh(): void {
        if (this.tokenExpiryTimeoutHandle) {
            clearTimeout(this.tokenExpiryTimeoutHandle);
            this.tokenExpiryTimeoutHandle = undefined;
        }

        if (!this.keycloak?.tokenParsed?.exp) {
            return;
        }

        const timeSkew = this.keycloak.timeSkew ?? 0;
        const expiresInMs = (this.keycloak.tokenParsed.exp - Date.now() / 1000 + timeSkew) * 1000;
        if (expiresInMs <= 0) {
            void this.updateToken();
            return;
        }

        this.tokenExpiryTimeoutHandle = setTimeout(() => {
            void this.updateToken();
        }, expiresInMs);
    }

    /**
     * Returns current shareable auth state when tab is authenticated.
     */
    private getCurrentTokenState(): SharedTokenState | undefined {
        if (!this.keycloak?.token || !this.keycloak.refreshToken) {
            return undefined;
        }

        return {
            token: this.keycloak.token,
            refreshToken: this.keycloak.refreshToken,
            idToken: this.keycloak.idToken,
            timeSkew: this.keycloak.timeSkew
        };
    }

    /**
     * Proactively publishes current token state to sibling tabs.
     */
    private broadcastTokenState(): void {
        const state = this.getCurrentTokenState();
        if (!state || !this.syncChannel) {
            return;
        }

        const msg: TokenStateMessage = {
            type: 'token-state',
            fromTabId: this.tabId,
            state
        };
        this.syncChannel.postMessage(msg);
    }

    /**
     * Minimal JWT payload decoding utility used for expiration comparisons.
     */
    private parseJwt(token?: string): Record<string, unknown> | undefined {
        if (!token) {
            return undefined;
        }

        const tokenPart = token.split('.')[1];
        if (!tokenPart) {
            return undefined;
        }

        const normalized = tokenPart.replace(/-/g, '+').replace(/_/g, '/');
        const decoded = atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '='));
        return JSON.parse(decoded) as Record<string, unknown>;
    }

    /**
     * Reads token `exp` claim as epoch seconds.
     */
    private extractTokenExp(token?: string): number | undefined {
        const parsed = this.parseJwt(token);
        const exp = parsed?.['exp'];
        return typeof exp === 'number' ? exp : undefined;
    }

    /**
     * Generates a per-tab random identifier.
     */
    private generateTabId(): string {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }
        return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }
}
